import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { Login } from './auth/Login';
import { ProvedorSessao, useSessao } from './auth/SessaoContext';
import { Equipe } from './paginas/Equipe';
import { Home } from './paginas/Home';
import { Campanhas, Marketing } from './paginas/Marketing';
import { Changelog } from './paginas/changelog/Changelog';
import { DevBoard } from './paginas/devboard/DevBoard';
import { MinhaPagina } from './paginas/eu/MinhaPagina';
import { AreasETier } from './paginas/gamedesign/AreasETier';
import { Pericias } from './paginas/gamedesign/PaginaDePericias';
import { CenasAcoesFluxo } from './paginas/gamedesign/CenasAcoesFluxo';
import { Gdd } from './paginas/gdd/Gdd';
import { Combate } from './paginas/gamedesign/Combate';
import { AcoesDeCombate } from './paginas/gamedesign/AcoesDeCombate';
import { Fichas } from './paginas/mistikdream/Fichas';
import { Registro } from './paginas/mistikdream/Registro';
import { Ficha } from './paginas/gamedesign/Ficha';
import { Personagem } from './paginas/gamedesign/Personagem';
import { CondicoesDoPersonagem } from './paginas/gamedesign/CondicoesDoPersonagem';
import { Progresso } from './paginas/Progresso';
import { MundoCalendario } from './paginas/mundo/calendario/MundoCalendario';
import { MundoCenas } from './paginas/mundo/cenas/MundoCenas';
import { MundoFicha } from './paginas/mundo/MundoFicha';
import { MundoInicio, MundoLista } from './paginas/mundo/MundoLista';
import { MundoMapa } from './paginas/mundo/MundoMapa';
import { Roadmap } from './paginas/Roadmap';
import { Acessos } from './paginas/acessos/Acessos';
import { Transcricoes } from './paginas/transcricoes/Transcricoes';
import { AppShell } from './shell/AppShell';
import { CodexImagens } from './paginas/CodexImagens';

/**
 * Documentos entra por carregamento tardio.
 *
 * <p>Ela é a única tela que precisa de um renderizador de markdown e de um
 * higienizador de HTML — juntos, uns 28 KB comprimidos. Importados de forma comum,
 * eles iriam no pacote principal, e quem só abre o Roadmap pagaria por eles em toda
 * visita. Assim descem na primeira vez que alguém abre o acervo, e ficam em cache.
 */
const Documentos = lazy(() =>
  import('./paginas/documentos/Documentos').then((m) => ({ default: m.Documentos }))
);

function Rotas() {
  const { usuario, carregando } = useSessao();

  if (carregando) {
    return (
      <main style={{ display: 'grid', placeItems: 'center', height: '100%', color: 'var(--meta)' }}>
        abrindo o GG Pilot…
      </main>
    );
  }

  if (!usuario) return <Login />;

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route path="/" element={<Home />} />
        <Route path="/roadmap" element={<Roadmap />} />
        <Route path="/dev-board" element={<DevBoard />} />
        {/* Fora do menu, de propósito: por ora só é alcançada pelo botão no Dev Board. */}
        <Route path="/changelog" element={<Changelog />} />
        <Route path="/equipe" element={<Equipe />} />
        <Route path="/season1" element={<Progresso />} />
        {/* A página de quem está logado. Sem id na rota: o alvo é sempre a sessão. */}
        <Route path="/eu" element={<MinhaPagina />} />
        {/* A tela se chamava "/progresso" quando media o mundo inteiro. Virou a Season 1
            e a URL foi junto. O redirecionamento fica: o endereço antigo está em link
            salvo e em histórico de navegador, e um 404 no lugar de uma página que
            existe é o pior jeito de comunicar que ela mudou de nome. */}
        <Route path="/progresso" element={<Navigate to="/season1" replace />} />
        <Route path="/marketing" element={<Marketing />} />
        <Route path="/game-design/gdd" element={<Gdd />} />
        <Route path="/game-design/personagem" element={<Personagem />} />
        <Route path="/game-design/condicoes-do-personagem" element={<CondicoesDoPersonagem />} />
        <Route path="/game-design/ficha" element={<Ficha />} />
        <Route path="/game-design/combate" element={<Combate />} />
        <Route path="/game-design/acoes-de-combate" element={<AcoesDeCombate />} />
        <Route path="/game-design/cenas-acoes-fluxo" element={<CenasAcoesFluxo />} />
        <Route path="/game-design/areas-e-tier" element={<AreasETier />} />
        <Route path="/game-design/pericias" element={<Pericias />} />
        <Route path="/game-design/pericias/:pericia" element={<Pericias />} />
        <Route path="/campanhas" element={<Campanhas />} />
        <Route path="/codex/imagens" element={<CodexImagens />} />
        <Route path="/transcricoes" element={<Transcricoes />} />
        <Route path="/acessos" element={<Acessos />} />
        <Route path="/mistik-dream/fichas" element={<Fichas />} />
        <Route path="/mistik-dream/registro" element={<Registro />} />
        <Route
          path="/documentos"
          element={
            <Suspense fallback={<div className="mundo mu-nada">Carregando…</div>}>
              <Documentos />
            </Suspense>
          }
        />
        <Route path="/mundo" element={<MundoInicio />} />
        {/* Antes da rota genérica: "mapa" e "cenas" não são coleções de ficha. */}
        <Route path="/mundo/mapa" element={<MundoMapa />} />
        <Route path="/mundo/cenas" element={<MundoCenas />} />
        <Route path="/mundo/calendario" element={<MundoCalendario />} />
        <Route path="/mundo/:colecao" element={<MundoLista />} />
        <Route path="/mundo/:colecao/:id" element={<MundoFicha />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <ProvedorSessao>
      <BrowserRouter>
        <Rotas />
      </BrowserRouter>
    </ProvedorSessao>
  );
}
